<template>
    <aside
        class="
            right_todo_sidebar_wrp
            workspace_right_sidebar
        "
        :class="{
            'workspace_right_sidebar--expanded': expanded
        }"
        aria-label="Workspace tools"
    >
        <div class="right_todo_sidebar_list">
            <nav
                class="todo_sidebar_header_list"
                aria-label="Workspace tools"
            >
                <div
                    class="row"
                    role="tablist"
                >
                    <div
                        v-for="(tool, index) in toolTabs"
                        :key="tool.id"
                        class="col-3"
                    >
                        <div class="todo_list_box">
                            <div class="todo_list_box_center">
                                <button
                                    :id="'workspace-tab-' + tool.id"
                                    ref="toolTab"
                                    type="button"
                                    class="todo_list_box_icon"
                                    :class="{
                                        active:
                                            activeToolId === tool.id
                                    }"
                                    role="tab"
                                    :tabindex="
                                        activeToolId === tool.id
                                            ? 0
                                            : -1
                                    "
                                    :aria-controls="
                                        'workspace-panel-' + tool.id
                                    "
                                    :aria-selected="
                                        activeToolId === tool.id
                                            ? 'true'
                                            : 'false'
                                    "
                                    @click="selectTool(tool.id)"
                                    @keydown="
                                        handleTabKeydown(
                                            $event,
                                            index
                                        )
                                    "
                                >
                                    <span class="workspace_todo_txt">
                                        {{ tool.label }}
                                    </span>

                                    <span class="todo_tooltip_wrp">
                                        <WorkspaceToolIcon
                                            :name="tool.id"
                                        />

                                        <span
                                            class="todo_tooltip_text"
                                            role="tooltip"
                                        >
                                            {{ tool.label }}
                                        </span>
                                    </span>
                                </button>
                            </div>
                        </div>
                    </div>
                </div>
            </nav>

            <div
                :id="'workspace-panel-' + activeToolId"
                class="todo_tab_content_wrp"
                role="tabpanel"
                :aria-labelledby="
                    'workspace-tab-' + activeToolId
                "
            >
                <component
                    :is="activeToolComponent"
                    :key="activeToolId"
                />
            </div>

            <div class="slide_left_arrow">
                <button
                    type="button"
                    class="slide_left_arrow_btn"
                    :aria-label="toggleLabel"
                    :title="toggleLabel"
                    @click="$emit('toggle')"
                >
                    <i
                        class="fa"
                        :class="
                            expanded
                                ? 'fa-chevron-right'
                                : 'fa-chevron-left'
                        "
                        aria-hidden="true"
                    />
                </button>
            </div>
        </div>
    </aside>
</template>

<script>
import WorkspaceToolIcon from
    '@/components/private/workspace/common/WorkspaceToolIcon';

const NotesPanel = () => import(
    '@/components/private/workspace/notes/'
);
const BriefcasePanel = () => import(
    '@/components/private/workspace/briefcase/'
);
const SchedulerPanel = () => import(
    '@/components/private/workspace/schedulerData/'
);
const TodoPanel = () => import(
    '@/components/private/workspace/todo-app/'
);

const TOOL_TABS = Object.freeze([
    Object.freeze({
        id: 'notes',
        label: 'Notes',
        component: NotesPanel
    }),
    Object.freeze({
        id: 'briefcase',
        label: 'Briefcase',
        component: BriefcasePanel
    }),
    Object.freeze({
        id: 'scheduler',
        label: 'Scheduler',
        component: SchedulerPanel
    }),
    Object.freeze({
        id: 'todo_app',
        label: 'To-Do',
        component: TodoPanel
    })
]);

const TAB_NAVIGATION_KEYS = Object.freeze({
    ArrowLeft: -1,
    ArrowUp: -1,
    ArrowRight: 1,
    ArrowDown: 1
});

export default {
    name: 'WorkspaceRightSidebar',

    components: {
        WorkspaceToolIcon
    },

    props: {
        expanded: {
            type: Boolean,
            default: false
        },

        isMobile: {
            type: Boolean,
            default: false
        }
    },

    data() {
        return {
            activeToolId: TOOL_TABS[0].id,
            toolTabs: TOOL_TABS
        };
    },

    computed: {
        activeTool() {
            return this.toolTabs.find((tool) => {
                return tool.id === this.activeToolId;
            }) || this.toolTabs[0];
        },

        activeToolComponent() {
            return this.activeTool.component;
        },

        toggleLabel() {
            if (this.isMobile) {
                return 'Close workspace tools';
            }

            return this.expanded
                ? 'Show chat list'
                : 'Expand workspace tools';
        }
    },

    methods: {
        getToolIndex(toolId) {
            return this.toolTabs.findIndex((tool) => {
                return tool.id === toolId;
            });
        },

        selectTool(toolId) {
            if (this.getToolIndex(toolId) === -1) {
                return;
            }

            this.activeToolId = toolId;
        },

        focusTool(index) {
            const tabButtons = this.$refs.toolTab || [];
            const target = tabButtons[index];

            if (target && typeof target.focus === 'function') {
                target.focus();
            }
        },

        activateToolAt(index) {
            const toolCount = this.toolTabs.length;

            if (!toolCount) {
                return;
            }

            const normalizedIndex =
                (index + toolCount) % toolCount;

            this.activeToolId =
                this.toolTabs[normalizedIndex].id;

            this.$nextTick(() => {
                this.focusTool(normalizedIndex);
            });
        },

        handleTabKeydown(event, currentIndex) {
            if (!event) {
                return;
            }

            if (event.key === 'Home') {
                event.preventDefault();
                this.activateToolAt(0);
                return;
            }

            if (event.key === 'End') {
                event.preventDefault();
                this.activateToolAt(
                    this.toolTabs.length - 1
                );
                return;
            }

            const direction =
                TAB_NAVIGATION_KEYS[event.key];

            if (!direction) {
                return;
            }

            event.preventDefault();

            this.activateToolAt(
                currentIndex + direction
            );
        }
    }
};
</script>

<style lang="scss" scoped>
.workspace_right_sidebar {
    width: auto;
    min-width: 0;
    height: 100%;
    padding-left: 10px;
    box-sizing: border-box;
    flex: 0 0 calc(32% - 2px);
    float: none;
    transition: flex-basis 0.5s ease-in-out;

    &--expanded {
        flex-basis: 50%;

        .workspace_todo_txt {
            position: relative;
            max-width: 120px;
            overflow: visible;
            visibility: visible;
            opacity: 1;
        }

        .todo_tooltip_text {
            display: none !important;
        }
    }
}

.right_todo_sidebar_list {
    position: relative;
    height: 100%;
    padding: 0 5px;
    overflow: hidden;
    background: rgba(0, 0, 0, 0.75);
    border-radius: 5px;
    box-shadow:
        5px 5px 10px rgba(138, 145, 153, 0.06),
        -5px 5px 10px rgba(138, 145, 153, 0.06);
}

.todo_sidebar_header_list {
    min-height: 50px;

    .row {
        margin: 0;
    }

    .col-3 {
        padding: 0 5px;
    }
}

.todo_list_box_icon {
    position: relative;
    display: flex;
    width: 100%;
    min-width: 0;
    padding: 8px 0;
    color: #ffc107;
    font: inherit;
    text-align: center;
    cursor: pointer;
    background: rgba(255, 255, 255, 0.1);
    border: 0;
    border-radius: 3px;
    align-items: center;
    justify-content: center;

    &:focus {
        outline: none;
    }

    &:focus-visible {
        outline: 2px solid #87cefa;
        outline-offset: 2px;
    }

    &.active {
        color: #000;
        background: #87cefa;

        .workspace_tool_icon--image {
            filter: brightness(0);
        }
    }
}

.workspace_todo_txt {
    position: absolute;
    max-width: 0;
    margin-right: 12px;
    overflow: hidden;
    color: inherit;
    font-family: "MyriadProRegular", "Lato", sans-serif;
    font-size: 16px;
    line-height: 1;
    white-space: nowrap;
    visibility: hidden;
    opacity: 0;
    transition:
        opacity 0.3s ease,
        max-width 0.3s ease;
}

.todo_tooltip_wrp {
    position: relative;
    display: inline-flex;
    cursor: pointer;
}

.workspace_tool_icon {
    display: block;
    width: 35px;
    height: 35px;
}

.workspace_tool_icon--image {
    width: 30px;
    height: 30px;
    object-fit: contain;
}

.todo_tooltip_text {
    position: absolute;
    top: calc(100% + 17px);
    left: -30px;
    z-index: 1000;
    display: none;
    width: 95px;
    margin: 0;
    padding: 2px 5px;
    color: #000;
    font-family: "MyriadProRegular", "Lato", sans-serif;
    font-size: 16px;
    font-weight: 600;
    line-height: 1.4;
    text-align: center;
    cursor: default;
    background: #ffdee3;
    border-radius: 2px;

    &::before {
        position: absolute;
        top: -5px;
        left: 43px;
        z-index: 1000;
        display: block;
        content: "";
        border: solid;
        border-color: #ffdee3 transparent;
        border-width: 12px 12px 0 0;
        transform: rotate(45deg);
    }
}

.todo_list_box_icon:hover,
.todo_list_box_icon:focus-visible {
    .todo_tooltip_text {
        display: block;
    }
}

.todo_tab_content_wrp {
    height: calc(100% - 50px);
    min-height: 0;
    padding: 10px 5px 0;
    overflow: auto;
}

.slide_left_arrow {
    position: absolute;
    top: 42%;
    left: 0;
    z-index: 2;
}

.slide_left_arrow_btn {
    display: block;
    width: 20px;
    height: 90px;
    padding: 0;
    color: #ffd400;
    font-size: 20px;
    line-height: 90px;
    text-align: center;
    cursor: pointer;
    background: rgba(79, 79, 79, 0.5);
    border: 0;
    border-radius: 4px;

    &:hover,
    &:focus-visible {
        color: #22292f;
        background: #87cefa;
        outline: none;
    }
}

@media only screen and (min-width: 768px) and (max-width: 1310px) {
    .todo_list_box_icon {
        height: 45px;
        padding: 5px 0;
        line-height: 42px;
    }

    .workspace_tool_icon {
        width: 28px;
        height: 28px;
    }

    .workspace_tool_icon--image {
        width: 22px;
        height: 22px;
    }
}

@media only screen and (max-width: 767px) {
    .workspace_right_sidebar {
        position: relative;
        z-index: 99;
        display: none !important;
        width: 100%;
        height: 100%;
        padding-left: 0;
        flex: none;
    }
}

@media (prefers-reduced-motion: reduce) {
    .workspace_right_sidebar,
    .workspace_todo_txt {
        transition: none;
    }
}
</style>
